import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ReturnsListPage from "@/pages/ReturnsListPage";
import ReturnNewPage from "@/pages/ReturnNewPage";
import ReturnDetailPage from "@/pages/ReturnDetailPage";
import QueuePage from "@/pages/QueuePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminMastersPage from "@/pages/AdminMastersPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import ReportsPage from "@/pages/ReportsPage";

// Role arrays hoisted to module scope so React doesn't see a new array
// identity on every App render, which would otherwise force ProtectedRoute
// to re-render needlessly.
const ROLES_NEW_RETURN = ["customer", "inward", "admin"];
const ROLES_INWARD = ["inward", "admin"];
const ROLES_QUALITY = ["quality", "admin"];
const ROLES_ACCOUNTS = ["accounts", "admin"];
const ROLES_ADMIN_ONLY = ["admin"];

// Status arrays for queue pages — same reasoning as above.
const INWARD_QUEUE_STATUSES = ["Submitted", "Material In Transit"];
const INSPECTION_QUEUE_STATUSES = ["Pending Inspection"];
const CREDIT_NOTE_QUEUE_STATUSES = [
  "Approved", "Partially Approved", "Credit Note Pending", "Credit Note Uploaded",
];

const Layout = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/change-password" element={
              <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
            } />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />

            <Route path="/returns" element={<Layout><ReturnsListPage /></Layout>} />
            <Route path="/returns/new" element={
              <ProtectedRoute roles={ROLES_NEW_RETURN}>
                <AppLayout><ReturnNewPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/returns/:id" element={<Layout><ReturnDetailPage /></Layout>} />

            <Route path="/inward" element={
              <ProtectedRoute roles={ROLES_INWARD}>
                <AppLayout>
                  <QueuePage
                    title="Inward Queue"
                    subtitle="Submitted requests waiting for material receipt"
                    statuses={INWARD_QUEUE_STATUSES}
                    testId="inward-page"
                  />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/inspection" element={
              <ProtectedRoute roles={ROLES_QUALITY}>
                <AppLayout>
                  <QueuePage
                    title="Inspection Queue"
                    subtitle="Material ready for quality inspection"
                    statuses={INSPECTION_QUEUE_STATUSES}
                    testId="inspection-page"
                  />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/credit-notes" element={
              <ProtectedRoute roles={ROLES_ACCOUNTS}>
                <AppLayout>
                  <QueuePage
                    title="Credit Notes Queue"
                    subtitle="Approved requests awaiting credit note"
                    statuses={CREDIT_NOTE_QUEUE_STATUSES}
                    testId="credit-notes-page"
                  />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/reports" element={<Layout><ReportsPage /></Layout>} />
            <Route path="/admin/users" element={
              <ProtectedRoute roles={ROLES_ADMIN_ONLY}><AppLayout><AdminUsersPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/admin/masters" element={
              <ProtectedRoute roles={ROLES_ADMIN_ONLY}><AppLayout><AdminMastersPage /></AppLayout></ProtectedRoute>
            } />
            <Route path="/audit-logs" element={
              <ProtectedRoute roles={ROLES_ADMIN_ONLY}><AppLayout><AuditLogsPage /></AppLayout></ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
